// AgentTool wrapper around the isolated Python realm.

import { Type } from "@earendil-works/pi-ai";
import { runPythonInRealm } from "./pythonRealmRuntime.js";
import {
    RUN_SURFACES,
    asRunContext,
    createUnknownRunContext,
    resolvedKernelFromModel,
} from "./kernelResolver.js";
import { createWheelConsentHandler } from "./runConsent.js";
import { getResult, listAvailable, recordResult } from "./toolResultStore.js";
import { resolveStoredOrRehydrate } from "./toolResultRehydrate.js";

const RESULT_TEXT_MAX = 4000;

// The realm reports every refusal with one fixed message, whatever the cause.
// Read as "Python is gone", it made the model drop Python for the rest of the
// chat even when the fix was one declared package or a smaller cell.
const RETRYABLE_REALM_REASONS = new Set([
    "bootstrap_failed",
    "bootstrap_timeout",
    "message_delivery_failed",
    "realm_controller_superseded",
    "realm_not_ready",
    "run_id_collision",
    "runtime_asset_unavailable",
]);

export function pythonFailureText(ret) {
    const reason = typeof ret?.reason === "string" && ret.reason ? ret.reason : null;
    const tag = reason ? ` (reason: ${reason})` : "";
    switch (reason) {
        case "package_requirement_undeclared": {
            const missing = Array.isArray(ret.missingPackages) ? ret.missingPackages : [];
            if (missing.length > 0) {
                const pronoun = missing.length === 1 ? "it" : "them";
                return `error: The code imports ${missing.join(", ")}, but \`packages\` does not list ${pronoun}. Run the call again with packages: ${JSON.stringify(missing)}.${tag}`;
            }
            return `error: The code imports a package that \`packages\` does not list. Declare every numpy, pandas or scipy import in \`packages\` and run the call again.${tag}`;
        }
        case "package_requirements_invalid":
            return `error: \`packages\` can list only numpy, pandas and scipy. Remove the others and run the call again.${tag}`;
        case "run_timeout":
            return `error: Python stopped this cell at its time limit, and nothing it computed was kept. Filter the input to the rows and columns the cell needs, or split the work across smaller calls.${tag}`;
        case "result_invalid":
            return `error: The cell finished, but its result was too large or not plain data, so it could not be returned. Return a smaller value, such as an aggregate, the first rows, or a list of plain dicts.${tag}`;
        case "realm_destroyed":
            return `error: Python was reset while this cell ran. Run the call again.${tag}`;
        default:
            break;
    }
    if (ret?.outcome === "unavailable") {
        if (reason && RETRYABLE_REALM_REASONS.has(reason)) {
            return `error: Python could not start for this call. Run it once more; if it fails again, continue without Python.${tag}`;
        }
        return `error: Python cannot run in this browser session, so running the call again will not help. Continue without Python and say that Python was unavailable.${tag}`;
    }
    return `error: ${ret?.error || "unknown error"}${tag}`;
}

export function createPythonTool({
    getNamespaceKey,
    rehydrate,
    runContext,
    getRunContext,
} = {}) {
    const fallbackRunContext = runContext
        ? asRunContext(runContext)
        : createUnknownRunContext({ surface: RUN_SURFACES.CHAT });
    return {
        name: "execute_python",
        label: "Execute Python",
        description:
            "Run Python in an isolated in-browser interpreter for data transformations the analytics DSL can't express (regex parsing, pivots, rolling windows, joins, etc.). Every call starts with fresh Python globals: complete dependent work in one call, and pass prior results explicitly with from_tool_calls.\n\n" +
            "Reading rows from a prior tool call — this is a correctness decision, NOT a style preference:\n" +
            "  • If your input rows came from an EARLIER tool call (analytics_query, prior execute_python, expand_preview, a spawn_subagent result), reference them via `from_tool_calls: { \"<global>\": \"<tool_call_id>\" }`. The full, untruncated rows are injected into the namespace under your chosen name before your code runs. This works EVEN WHEN the LLM-visible result was preview-clipped (analytics_query returning > 100 rows shows you only the first 20 in your context, but `from_tool_calls` always pipes in the complete set). The `tool_call_id` is the `_tool_call_id` field echoed in the prior result — copy it verbatim.\n" +
            "  • Do NOT inline rows from a prior tool result by retyping them as a Python list literal in `args.code`, and do NOT paste them into `args.data` to work around the preview. Inlining (a) wastes output tokens encoding rows the system already has, (b) silently freezes the dataset instead of using the query's complete result, and (c) breaks data lineage so audit tools can't trace producers / consumers. If the preview-clipped rows are all you have access to, the answer is from_tool_calls, not inline literals.\n" +
            "  • `args.data` is for one-off JSON blobs that DIDN'T come from a prior tool call (constants, a hardcoded lookup table, an external input). Never use it as a workaround for from_tool_calls.\n\n" +
            "Workflow notes:\n" +
            "  • Stdlib is built in. Declare any pinned optional package you use in `packages`; numpy, pandas, and scipy load lazily.\n" +
            "  • The shipped isolated-realm protocol does not return `print(...)` output or live progress. Put the value you need to inspect in the LAST EXPRESSION (notebook style). Use `df.to_dict('records')` for DataFrames if you want plain rows — the tool auto-converts pandas DataFrame/Series but explicit is fine too.\n\n" +
            "When NOT to use: if the analytics DSL can express it (groupBy, aggregates, filters), prefer analytics_query — it runs server-side and is far cheaper. Reach for this tool for the residual cases.",
        parameters: Type.Object({
            code: Type.String({
                description:
                    "Python source. The last expression's value is returned. Pass prior rows with from_tool_calls.",
            }),
            packages: Type.Optional(
                Type.Array(
                    Type.Union([
                        Type.Literal("numpy"),
                        Type.Literal("pandas"),
                        Type.Literal("scipy"),
                    ]),
                    {
                        description:
                            "Pinned Python package roots required by this call. Declare only packages imported by the code; dependencies are resolved from the verified manifest.",
                        maxItems: 3,
                        uniqueItems: true,
                    },
                ),
            ),
            data: Type.Optional(
                Type.Unknown({
                    description:
                        "JSON-serialisable input lands as the global `data` in the namespace before your code runs. Only for one-off blobs that DIDN'T come from a prior tool call (constants, external inputs). For rows produced by an earlier tool call, use `from_tool_calls` — `data` is not a workaround for the preview cutoff.",
                }),
            ),
            from_tool_calls: Type.Optional(
                Type.Record(Type.String(), Type.String(), {
                    description:
                        "Map of { python_global_name: tool_call_id } that injects the FULL untruncated rows from earlier tool calls into the python namespace. Use this whenever your input came from analytics_query / execute_python / expand_preview / a spawn_subagent result — it's the right path even when the LLM-visible preview was clipped (e.g. analytics_query > 100 rows). Copy the `_tool_call_id` verbatim from the prior result. Unknown ids return a typed error listing what IS available.",
                }),
            ),
        }),
        async execute(toolCallId, args, signal, onUpdate) {
            const state = {
                phase: "loading",
                stdout: "",
                stderr: "",
                packagesLoaded: [],
                result: null,
                error: null,
            };
            const emit = () => {
                try { onUpdate?.({ details: { ...state } }); } catch { /* noop */ }
            };
            emit();

            // Guard against empty / whitespace-only code. Pyodide
            // would otherwise accept the empty string, emit nothing,
            // and return — but the surrounding state machine streams
            // no events back to the chat, so the tool node stays
            // wedged in "loading" until the user stops the run. Fail
            // loud instead with a typed error the agent can recover
            // from on the next turn.
            const code = typeof args?.code === "string" ? args.code : "";
            if (!code.trim()) {
                const message = "execute_python: `code` is required and must be non-empty. If you only wanted to inject data, include the code that uses it (e.g. `df = pd.DataFrame(data); df.describe()`).";
                state.phase = "done";
                state.error = message;
                emit();
                return {
                    content: [{ type: "text", text: message }],
                    details: { ...state },
                    isError: true,
                };
            }

            const namespaceKey = getNamespaceKey?.() || "default";

            // Resolve `from_tool_calls` before invoking pyodide. Each
            // entry's rows get injected as a named global; the
            // resolvedInputs metadata feeds the UI's debug surface
            // (ARGS → RESOLVED INPUTS → RESULT). A missing-id
            // failure here short-circuits — there's no point running
            // python against half its declared inputs, and the
            // error message surfaces the actual available ids so
            // the next turn self-corrects.
            const resolvedInputs = [];
            const resolvedGlobals = {};
            const refs = args?.from_tool_calls;
            let lookupError = null;
            if (refs && typeof refs === "object") {
                for (const [name, refId] of Object.entries(refs)) {
                    if (typeof refId !== "string" || !refId.trim()) {
                        resolvedInputs.push({ name, refId: String(refId || ""), error: "missing tool call id" });
                        if (!lookupError) lookupError = `from_tool_calls.${name}: missing tool call id`;
                        continue;
                    }
                    // resolveStoredOrRehydrate checks the store first;
                    // on miss / evicted marker, it calls rehydrate
                    // (Phase 3) to re-run the original analytics_query
                    // spec under the same toolCallId. Transparent to
                    // the LLM — the result lands in the same store
                    // key the caller asked for.
                    const { stored, rehydrated, reason } = await resolveStoredOrRehydrate({
                        namespaceKey,
                        toolCallId: refId,
                        rehydrate,
                        signal,
                    });
                    if (!stored || !Array.isArray(stored.rows)) {
                        resolvedInputs.push({
                            name,
                            refId,
                            error: reason ? `not available (${reason})` : "no result stored under this id",
                        });
                        if (!lookupError) {
                            lookupError = `from_tool_calls.${name}: ${reason ? `${reason} (id ${refId})` : `no result stored under ${refId}`}`;
                        }
                        continue;
                    }
                    resolvedGlobals[name] = stored.rows;
                    resolvedInputs.push({
                        name,
                        refId,
                        source: stored.source,
                        rowCount: stored.rows.length,
                        rehydrated: rehydrated || undefined,
                    });
                }
            }
            if (resolvedInputs.length) {
                state.resolvedInputs = resolvedInputs;
                emit();
            }
            if (lookupError) {
                const catalog = listAvailable(namespaceKey, { limit: 8 });
                const catalogText = catalog.length
                    ? "\n\nAvailable row-shaped results in this chat (most recent first):\n" +
                      catalog.map((e) => `  • ${e.id}  (${e.source}, ${e.rowCount} rows)`).join("\n") +
                      "\n\nCopy the id verbatim from the list above into from_tool_calls and retry."
                    : "\n\nNo row-shaped tool results are currently stored — call analytics_query first.";
                state.phase = "done";
                state.error = lookupError;
                emit();
                return {
                    content: [{ type: "text", text: `error: ${lookupError}.${catalogText}` }],
                    details: { ...state },
                    isError: true,
                };
            }

            let pythonRunContext = fallbackRunContext;
            if (typeof getRunContext === "function") {
                try {
                    const current = getRunContext();
                    pythonRunContext = current
                        ? asRunContext(current)
                        : fallbackRunContext;
                } catch {
                    pythonRunContext = createUnknownRunContext({
                        surface: RUN_SURFACES.CHAT,
                    });
                }
            }
            const resolvedDefinition = resolvedKernelFromModel(
                { kind: "execute_python", code },
                pythonRunContext,
            );
            const ret = await runPythonInRealm({
                resolvedDefinition,
                runContext: pythonRunContext,
                code,
                packageRequirements: args?.packages,
                globals: {
                    ...resolvedGlobals,
                    ...(args.data === undefined ? {} : { data: args.data }),
                },
                attribution: {
                    kind: "execute_python",
                    toolCallId,
                    namespaceKey,
                },
                onWheelRequest: createWheelConsentHandler(
                    pythonRunContext,
                    { signal },
                ),
                runId: toolCallId,
                // STOP tears down the isolated realm when Python cannot yield.
                signal,
            });

            state.phase = "done";
            state.stdout = ret.stdout || state.stdout;
            state.stderr = ret.stderr || state.stderr;
            state.packagesLoaded = ret.packagesLoaded || [];
            if (ret.ok) {
                state.result = ret.result;
                state.error = null;
            } else {
                state.error = ret.error || "unknown error";
                state.outcome = ret.outcome || "failed";
                if (ret.reason) state.reason = ret.reason;
                if (Array.isArray(ret.missingPackages)) state.missingPackages = [...ret.missingPackages];
            }
            emit();

            if (!ret.ok) {
                const summary = [
                    pythonFailureText(ret),
                    state.stderr ? `\nstderr:\n${state.stderr}` : "",
                    state.stdout ? `\nstdout:\n${state.stdout}` : "",
                ].join("");
                return {
                    content: [{ type: "text", text: trim(summary, RESULT_TEXT_MAX) }],
                    details: { ...state },
                    isError: true,
                };
            }

            // If the python step produced a row-shaped result (list
            // of dicts), record it under this call's id so a later
            // execute_python can reference it via from_tool_calls.
            // Non-row results aren't useful as inputs to downstream
            // tools, so we skip them silently.
            let recordedAsRef = false;
            try {
                if (Array.isArray(state.result)
                    && state.result.length > 0
                    && typeof state.result[0] === "object"
                    && !Array.isArray(state.result[0])) {
                    recordResult(namespaceKey, toolCallId, state.result, "execute_python");
                    recordedAsRef = true;
                }
            } catch { /* recording is non-essential */ }

            const text = composeResultText(state, {
                toolCallId: recordedAsRef ? toolCallId : null,
            });
            return {
                content: [{ type: "text", text: trim(text, RESULT_TEXT_MAX) }],
                details: { ...state },
            };
        },
    };
}

function composeResultText(state, { toolCallId } = {}) {
    const parts = [];
    if (state.result === null || state.result === undefined) {
        parts.push("(no return value)");
    } else if (typeof state.result === "string") {
        parts.push(state.result);
    } else {
        try {
            parts.push(JSON.stringify(state.result, null, 2));
        } catch {
            parts.push(String(state.result));
        }
    }
    if (state.stdout) parts.push(`\n--- stdout ---\n${state.stdout}`);
    if (state.stderr) parts.push(`\n--- stderr ---\n${state.stderr}`);
    // When this call's row-shaped output was recorded for later
    // reference, surface the id so the model can re-emit it on the
    // next from_tool_calls without digging into prior tool_use
    // metadata.
    if (toolCallId) {
        parts.push(`\n--- _tool_call_id ---\n${toolCallId}`);
    }
    return parts.join("");
}

function trim(text, max) {
    if (!text) return "";
    if (text.length <= max) return text;
    return `${text.slice(0, max)}\n…[truncated]`;
}
